import type { CountryCode } from '@/graphql/saleor/api';
import { MOCK_DEFAULT_EMAIL, MOCK_DEFAULT_VALUE } from '@/lib/const';
import { ExpressOrderUpdateError } from '@/lib/expressOrder';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { paths } from '@/lib/paths';
import { PAYPAL_EXPRESS_QUERY_PARAMS } from '@/lib/payment/queryParams';
import { formatPhoneWithCallingCode } from '@/lib/phone';
import { getCurrentUrl } from '@/lib/util';

import type { PaypalPayer, PaypalShippingAddress } from './types';
import { isPhoneInvalidError } from './types';

export const getNamesFromShippingAddress = (
  shippingAddress?: PaypalShippingAddress,
) => {
  if (!shippingAddress?.firstName) return { firstName: '', lastName: '' };
  const name = shippingAddress.firstName?.trim() || '';
  const [firstName = '', ...rest] = name.split(/\s+/);
  const lastName = rest.join(' ');
  return { firstName, lastName };
};

export interface UpdateAddressWithRetryDeps<Shipping, Billing> {
  addressUpdate: (
    type: 'shipping' | 'billing',
    data: Shipping | Billing,
  ) => Promise<unknown>;
  billing: Billing;
  emailUpdate: (email: string) => Promise<unknown>;
  handlePromiseErrors: (results: PromiseSettledResult<unknown>[]) => Error[];
  orderId: string | undefined;
  payerCountryCode?: string;
  payerNationalNumber?: string;
  shipping: Shipping;
  shopperEmail?: string;
}

export const updateAddressWithRetry = async <
  Shipping extends { phone?: string },
  Billing extends { phone?: string },
>(
  deps: UpdateAddressWithRetryDeps<Shipping, Billing>,
  attempt = 0,
): Promise<void> => {
  const {
    addressUpdate,
    billing,
    emailUpdate,
    handlePromiseErrors,
    orderId,
    payerCountryCode,
    payerNationalNumber,
    shipping,
    shopperEmail,
  } = deps;

  if (!orderId) return;

  const national = (payerNationalNumber || '').trim();
  const nationalWithPlus = national
    ? national.startsWith('+')
      ? national
      : `+${national}`
    : '';
  const formatted = formatPhoneWithCallingCode(national, payerCountryCode);
  const phoneCandidates = [nationalWithPlus, formatted, ''].filter(
    (v, i, arr) => typeof v === 'string' && i === arr.findIndex((x) => x === v),
  ) as string[];

  const candidatePhone = phoneCandidates[attempt] ?? '';
  shipping.phone = candidatePhone;
  billing.phone = candidatePhone;

  const tasks: Promise<unknown>[] = [
    addressUpdate('shipping', shipping),
    addressUpdate('billing', billing),
  ];

  if (shopperEmail) {
    tasks.push(emailUpdate(shopperEmail));
  }

  const results = await Promise.allSettled(tasks);
  const errors = handlePromiseErrors(
    results as PromiseSettledResult<unknown>[],
  );
  const checkoutPhoneError = errors.some(isPhoneInvalidError);

  const response = await fetch(paths.api.express.updateOrder.asPath(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: orderId,
      shippingAddress: shipping,
      billingAddress: billing,
      userEmail: shopperEmail || '',
    }),
  });

  if (response.ok) return;

  const body = await response.json();
  const phoneError = body.errors?.some(isPhoneInvalidError);

  if (phoneError || checkoutPhoneError) {
    const nextAttempt = attempt + 1;
    if (nextAttempt < phoneCandidates.length) {
      return updateAddressWithRetry(deps, nextAttempt);
    }
  }

  throw new ExpressOrderUpdateError(
    response.status,
    response.statusText,
    Array.isArray(body.errors) ? body.errors : [],
  );
};

const CANCEL_KEYWORDS = ['CANCEL', 'Window is closed', 'popup close'];

const containsCancelKeyword = (text: string): boolean => {
  return CANCEL_KEYWORDS.some((keyword) => text.includes(keyword));
};

const hasMessage = (err: object): err is { message: string } => {
  return (
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  );
};

export const isPaypalSdkLoadError = (err: unknown): boolean => {
  if (!err) return false;

  if (typeof err === 'object' && 'type' in err && err.type === 'SCRIPT_ERROR') {
    return true;
  }

  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : typeof err === 'object' && hasMessage(err)
          ? err.message
          : '';

  return (
    message.includes("reading 'FUNDING'") || message.includes('SCRIPT_ERROR')
  );
};

export interface ExpressOrderAbandonCheck {
  billingAddress?: {
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
  } | null;
  shippingAddress?: {
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
  } | null;
  userEmail?: string | null;
}

export const shouldAbandonExpressOrder = (
  order: ExpressOrderAbandonCheck | null | undefined,
): boolean => {
  if (!order) return false;

  return (
    order.userEmail === MOCK_DEFAULT_EMAIL &&
    (order.shippingAddress?.firstName === MOCK_DEFAULT_VALUE ||
      order.shippingAddress?.lastName === MOCK_DEFAULT_VALUE) &&
    order.shippingAddress?.phone === '' &&
    (order.billingAddress?.firstName === MOCK_DEFAULT_VALUE ||
      order.billingAddress?.lastName === MOCK_DEFAULT_VALUE) &&
    order.billingAddress?.phone === ''
  );
};

export const buildPaypalExpressReturnUrl = (orderId: string): string => {
  const returnUrl = new URL(getCurrentUrl());
  returnUrl.searchParams.set(PAYPAL_EXPRESS_QUERY_PARAMS.orderId, orderId);
  return returnUrl.toString();
};

const PAYPAL_EXPRESS_SUCCESS_REDIRECT_RESULT_CODES = new Set([
  'Authorised',
  'Pending',
]);

const PAYPAL_EXPRESS_REDIRECT_FAILURE_RESULT_CODES = new Set([
  'Refused',
  'Error',
  'Cancelled',
]);

type PaypalExpressQueryParam = string | string[] | undefined;

export type PaypalExpressQuery = Record<string, PaypalExpressQueryParam>;

const queryParamToString = (
  value: PaypalExpressQueryParam,
): string | undefined => {
  if (!value || Array.isArray(value)) return undefined;
  return value;
};

export const hasPaypalExpressSuccessRedirectReturnSearch = (
  search: string,
): boolean => {
  const params = new URLSearchParams(search);
  const query: PaypalExpressQuery = {};

  params.forEach((value, key) => {
    query[key] = value;
  });

  return isPaypalExpressSuccessRedirectReturn(query);
};

export const getPaypalExpressSuccessRedirectOrderIdFromSearch = (
  search: string,
): string | null => {
  if (!hasPaypalExpressSuccessRedirectReturnSearch(search)) {
    return null;
  }

  return new URLSearchParams(search).get(PAYPAL_EXPRESS_QUERY_PARAMS.orderId);
};

export type PaypalExpressSuccessRedirectQuery = {
  orderId: string;
  transactionId?: string;
};

export const getPaypalExpressSuccessRedirectFromQuery = (
  query: PaypalExpressQuery,
): PaypalExpressSuccessRedirectQuery | null => {
  const orderId = queryParamToString(
    query[PAYPAL_EXPRESS_QUERY_PARAMS.orderId],
  );
  const resultCode = queryParamToString(
    query[PAYPAL_EXPRESS_QUERY_PARAMS.additionalAction],
  );
  if (
    !orderId ||
    !resultCode ||
    !PAYPAL_EXPRESS_SUCCESS_REDIRECT_RESULT_CODES.has(resultCode)
  ) {
    return null;
  }

  const transactionId = queryParamToString(
    query[PAYPAL_EXPRESS_QUERY_PARAMS.transactionId],
  );

  return { orderId, transactionId };
};

export const isPaypalExpressSuccessRedirectReturn = (
  query: PaypalExpressQuery,
): boolean => getPaypalExpressSuccessRedirectFromQuery(query) !== null;

export const PAYPAL_EXPRESS_SESSION_KEYS = {
  stateData: 'breitling.pe_state_data',
  transactionRef: 'breitling.pe_transaction_ref',
} as const;

export type PaypalExpressRedirectSessionStorage = {
  getSessionStorage: (key: string) => unknown;
  setSessionStorage: (key: string, value: unknown) => void;
};

export const seedPaypalExpressRedirectSession = (
  storage: PaypalExpressRedirectSessionStorage,
  payload: PaypalExpressSuccessRedirectQuery,
): void => {
  if (payload.transactionId) {
    storage.setSessionStorage(
      PAYPAL_EXPRESS_SESSION_KEYS.transactionRef,
      payload.transactionId,
    );
  }

  if (!storage.getSessionStorage(PAYPAL_EXPRESS_SESSION_KEYS.stateData)) {
    storage.setSessionStorage(PAYPAL_EXPRESS_SESSION_KEYS.stateData, {});
  }
};

export const isPaypalExpressRedirectReturn = (
  query: PaypalExpressQuery,
): boolean => {
  const orderId = query[PAYPAL_EXPRESS_QUERY_PARAMS.orderId];
  if (!orderId || Array.isArray(orderId)) return false;

  const errorCode = query[PAYPAL_EXPRESS_QUERY_PARAMS.errorCode];
  if (errorCode && !Array.isArray(errorCode)) return true;

  const resultCode = query[PAYPAL_EXPRESS_QUERY_PARAMS.additionalAction];
  if (!resultCode || Array.isArray(resultCode)) return true;

  if (resultCode === 'Authorised' || resultCode === 'Pending') return false;

  return PAYPAL_EXPRESS_REDIRECT_FAILURE_RESULT_CODES.has(resultCode);
};

export const hasPaypalExpressFailedRedirectReturnSearch = (
  search: string,
): boolean => {
  const params = new URLSearchParams(search);
  const query: PaypalExpressQuery = {};

  params.forEach((value, key) => {
    query[key] = value;
  });

  return isPaypalExpressRedirectReturn(query);
};

export const getPaypalExpressRedirectOrderIdFromSearch = (
  search: string,
): string | null => {
  if (!hasPaypalExpressFailedRedirectReturnSearch(search)) {
    return null;
  }

  return new URLSearchParams(search).get(PAYPAL_EXPRESS_QUERY_PARAMS.orderId);
};

export const shouldRecoverPaypalExpressOnPageShow = (
  isInitialPageShow: boolean,
  persisted: boolean,
  isPaymentInProgress: boolean,
  hasFailedRedirectReturn = false,
): boolean => {
  if (hasFailedRedirectReturn) {
    return true;
  }

  if (isInitialPageShow) {
    return false;
  }

  return persisted || isPaymentInProgress;
};

export const isCancelError = (err: unknown): boolean => {
  if (!err) return false;

  if (typeof err === 'string') {
    return containsCancelKeyword(err);
  }

  if (typeof err === 'object') {
    if (err instanceof Error) {
      return (
        containsCancelKeyword(err.message) ||
        containsCancelKeyword(err.toString())
      );
    }

    const errorString = JSON.stringify(err);
    if (containsCancelKeyword(errorString)) {
      return true;
    }

    if (hasMessage(err)) {
      return containsCancelKeyword(err.message);
    }
  }

  return false;
};

export interface GetTransactionAmountParams {
  addressUpdated: boolean;
  checkoutGrossAmount: number | undefined;
  checkoutNetAmount: number | undefined;
  depositAmount: number | undefined;
  depositNetAmount: number | undefined;
  includeVAT: boolean;
  isDepositTransaction: boolean;
}

export const getTransactionAmount = ({
  addressUpdated,
  checkoutGrossAmount,
  checkoutNetAmount,
  depositAmount,
  depositNetAmount,
  includeVAT,
  isDepositTransaction,
}: GetTransactionAmountParams): number => {
  if (addressUpdated) {
    return isDepositTransaction
      ? (depositAmount ?? 0)
      : (checkoutGrossAmount ?? 0);
  }

  if (isDepositTransaction) {
    return includeVAT ? (depositAmount ?? 0) : (depositNetAmount ?? 0);
  }
  return includeVAT ? (checkoutGrossAmount ?? 0) : (checkoutNetAmount ?? 0);
};

export interface BuildShippingAddressInputParams {
  payerPhoneNumber: string;
  shippingAddress: PaypalShippingAddress | undefined;
  userPhoneFull: string;
}

export const buildShippingAddressInput = ({
  payerPhoneNumber,
  shippingAddress,
  userPhoneFull,
}: BuildShippingAddressInputParams) => {
  const { firstName, lastName } = getNamesFromShippingAddress(shippingAddress);

  return {
    firstName: firstName || '',
    lastName: lastName || '',
    companyName: '',
    streetAddress1: shippingAddress?.street || '',
    streetAddress2: '',
    city: shippingAddress?.city || '',
    cityArea: '',
    postalCode: shippingAddress?.postalCode || '',
    phone: userPhoneFull || payerPhoneNumber || '',
    country: (shippingAddress?.country as CountryCode) || '',
    countryArea: shippingAddress?.stateOrProvince || '',
    metadata: [
      {
        key: ACCOUNT_METADATA.phone,
        value: payerPhoneNumber,
      },
    ],
    skipValidation: true,
  };
};

export interface BuildBillingAddressInputParams {
  billingAddress: PaypalShippingAddress | undefined;
  payer: PaypalPayer | undefined;
  payerPhoneNumber: string;
  shippingAddress: PaypalShippingAddress | undefined;
  userPhoneFull: string;
}

export const buildBillingAddressInput = ({
  billingAddress,
  payer,
  payerPhoneNumber,
  shippingAddress,
  userPhoneFull,
}: BuildBillingAddressInputParams) => {
  return {
    firstName: payer?.name?.given_name || billingAddress?.firstName || '',
    lastName: payer?.name?.surname || billingAddress?.lastname || '',
    companyName: '',
    streetAddress1: billingAddress?.street || shippingAddress?.street || '',
    streetAddress2: '',
    city: billingAddress?.city || shippingAddress?.city || '',
    cityArea: '',
    postalCode: billingAddress?.postalCode || shippingAddress?.postalCode || '',
    phone: userPhoneFull || payerPhoneNumber || '',
    country:
      (billingAddress?.country as CountryCode) ||
      (shippingAddress?.country as CountryCode) ||
      '',
    countryArea:
      billingAddress?.stateOrProvince || shippingAddress?.stateOrProvince || '',
    metadata: [
      {
        key: ACCOUNT_METADATA.phone,
        value: payerPhoneNumber,
      },
    ],
  };
};
